import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { TextField, Box, Typography, FormControlLabel, Switch } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { projectsApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { useProjectPermissions } from '../../../hooks/useProjectPermissions';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmployeeSearch from '../../common/EmployeeSearch';
import ClientSelector from '../../common/ClientSelector';
import ClientContractTypeSelector from '../../common/ClientContractTypeSelector';
import BusinessAreaSelector from '../../common/BusinessAreaSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import ProjectScopeTypeSelector from '../../common/ProjectScopeTypeSelector';
import BillingTypeSelector from '../../common/BillingTypeSelector';
import ProjectPurposeSelector from '../../common/ProjectPurposeSelector';
import { PROJECT_PURPOSE_TYPES, shouldHideAdvancedFeatures } from '../../../constants/projectPurposes';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  BackButton,
  HeaderButton,
  FormContainer,
  TabContainer,
  TabButton,
  FormSection,
  SectionTitle,
  FormRow,
} from './styles';
import Dashboards from './components/Dashboards/Dashboards';
import Employees from './components/Employees/Employees';
import TeamStructure from './components/TeamStructure/TeamStructure';
import ProjectRadar from './components/ProjectRadar';
import { toPickerValue } from '../../../utils/dateOnly';

const TABS = {
  PROJECT_DATA: 'PROJECT_DATA',
  TEAM_STRUCTURE: 'TEAM_STRUCTURE',
  EMPLOYEES: 'EMPLOYEES',
  TECHNOLOGY_RADAR: 'TECHNOLOGY_RADAR',
  DASHBOARDS: 'DASHBOARDS'
};

const ProjectForm = ({ mode = 'add', projectId, onClose, readOnly = false, onBack, onProjectUpdate }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();
  const { canCreateProjectCode, canUpdateProjectCode, canUpdateProjectName } = useProjectPermissions();
  const [isLoading, setIsLoading] = useState(mode === 'edit' || mode === 'manage-employees' || mode === 'manage-code');
  const [activeTab, setActiveTab] = useState(mode === 'manage-employees' ? TABS.EMPLOYEES : TABS.PROJECT_DATA);
  const [businessUnitSelected, setBusinessUnitSelected] = useState(mode === 'edit' || mode === 'manage-employees' || mode === 'manage-code'); // Show all fields when editing, managing employees, or managing code
  const [formData, setFormData] = useState({
    name: '',
    code: null,
    description: '',
    startDate: '',
    endDate: '',
    contractTypeId: '',
    clientId: '',
    businessAreaId: '',
    managerId: '',
    businessUnitId: '',
    scope: '',
    timesheetRequired: false,
    billingType: '',
    projectPurposeId: ''
  });
  const [projectData, setProjectData] = useState(null);
  const [orgUnitTypeId, setOrgUnitTypeId] = useState(null);

  const [selectedManager, setSelectedManager] = useState(null);

  useEffect(() => {
    fetchMetadata();
    if ((mode === 'edit' || mode === 'manage-employees' || mode === 'manage-code') && id) {
      fetchProject();
    }
  }, [mode, id]);

  // Reset active tab if it becomes hidden due to project purpose
  useEffect(() => {
    if (shouldHideAdvancedTabs() && (activeTab === TABS.TECHNOLOGY_RADAR || activeTab === TABS.DASHBOARDS)) {
      setActiveTab(TABS.PROJECT_DATA);
    }
  }, [projectData?.projectPurpose]);

  const fetchMetadata = async () => {
    try {
      setIsLoading(true);
      // Metadata is now handled by individual selector components
    } catch (error) {
      console.error('Error fetching metadata:', error);
      showAlert('Failed to load form data', 'error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProject = async () => {
    try {
      setIsLoading(true);
      const projectData = await projectsApi.getProjectById(id);
      setProjectData(projectData); // Store the full project data
      
      setFormData({
        name: projectData.name,
        code: projectData.code || null,
        description: projectData.description,
        startDate: projectData.startDate || '',
        endDate: projectData.endDate || '',
        contractTypeId: projectData.contractType?.id || '',
        clientId: projectData.client?.id || '',
        businessAreaId: projectData.businessArea?.id || '',
        businessUnitId: projectData.businessUnit?.id || '',
        managerId: projectData.manager?.id || '',
        scope: projectData.scope || '',
        timesheetRequired: projectData.timesheetRequired || false,
        billingType: projectData.billingType,
        projectPurposeId: projectData.projectPurpose?.id || ''
      });
      // Set the selected manager for the EmployeeSearch component
      setSelectedManager(projectData.manager || null);
      
      // Set the orgUnitTypeId from the business unit
      if (projectData.businessUnit?.orgUnitType?.id) {
        setOrgUnitTypeId(projectData.businessUnit.orgUnitType.id);
        // Don't apply defaults when editing existing project - keep current values
        // await applyProjectTypePolicyDefaults(projectData.businessUnit.orgUnitType.id);
      }
    } catch (err) {
      showAlert(err.message || 'Failed to fetch project', 'error', err);
      if (onBack) {
        onBack();
      } else {
        navigate('/projects');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBusinessUnitDetails = async (businessUnitId) => {
    try {
      if (businessUnitId) {
        const businessUnitData = await organizationApi.getBusinessUnit(businessUnitId);
        if (businessUnitData?.orgUnitType?.id) {
          setOrgUnitTypeId(businessUnitData.orgUnitType.id);
          // Only apply defaults in add mode
          if (mode === 'add') {
            await applyProjectTypePolicyDefaults(businessUnitData.orgUnitType.id);
          }
        } else {
          setOrgUnitTypeId(null);
        }
      } else {
        setOrgUnitTypeId(null);
      }
    } catch (error) {
      console.error('Error fetching business unit details:', error);
      setOrgUnitTypeId(null);
    }
  };

  // Check if tabs should be hidden based on project purpose
  const shouldHideAdvancedTabs = () => {
    return shouldHideAdvancedFeatures(projectData?.projectPurpose);
  };

  const applyProjectTypePolicyDefaults = async (orgUnitTypeId) => {
    try {
      const policies = await projectsApi.getAllProjectTypePolicies({ orgUnitTypeId });
      
      // Find the default policy (isDefault: true)
      const defaultPolicy = policies.find(policy => policy.isDefault);
      
      if (defaultPolicy) {
        // Apply defaults from the policy
        setFormData(prev => ({
          ...prev,
          projectPurposeId: defaultPolicy.projectPurpose?.id || prev.projectPurposeId,
          billingType: defaultPolicy.defaultBillingType !== undefined ? defaultPolicy.defaultBillingType : prev.billingType,
          timesheetRequired: defaultPolicy.defaultTimesheetRequired ?? prev.timesheetRequired,
          scope: defaultPolicy.defaultProjectScope || prev.scope
        }));
      }
    } catch (error) {
      console.error('Error fetching project type policies:', error);
      // Don't show alert for this as it's not critical - form can still be used manually
    }
  };

  const handleInputChange = (field) => (event) => {
    let value = event?.target?.value ?? event;

    // Convert empty string to null for code field
    if (field === 'code' && value === '') {
      value = null;
    }

    // When business unit changes, fetch its org unit type and clear project purpose
    if (field === 'businessUnitId') {
      fetchBusinessUnitDetails(value);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        projectPurposeId: '' // Reset project purpose when business unit changes
      }));
      // Enable the rest of the form when business unit is selected for new projects
      if (mode === 'add' && value) {
        setBusinessUnitSelected(true);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleManagerChange = (employee) => {
    setSelectedManager(employee);
    setFormData(prev => ({
      ...prev,
      managerId: employee?.id || ''
    }));
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    // Validate required fields (skip validation in manage-code mode)
    if (mode !== 'manage-code' && (!formData.name || !formData.startDate || !formData.clientId || !formData.businessUnitId)) {
      showAlert('Please fill in all required fields: Project Name, Start Date, Client, and Business Unit', 'error');
      return;
    }

    try {
      if (mode === 'add') {
        await projectsApi.createProject(formData);
        showAlert('Project created successfully', 'success');
      } else {
        await projectsApi.updateProject(id, formData);
        const successMessage = mode === 'manage-code' ? 'Project code updated successfully' : 'Project updated successfully';
        showAlert(successMessage, 'success');
        // Update the project in the store
        if (onProjectUpdate) {
          onProjectUpdate();
        }
      }
      if (onBack) {
        onBack();
      } else {
        navigate('/projects');
      }
    } catch (err) {
      showAlert(err.message || `Failed to ${mode} project`, 'error', err);
    }
  };

  const renderProjectData = () => {
    const projectDataReadonly = isTabReadonly(TABS.PROJECT_DATA);
    const isManageCodeMode = mode === 'manage-code';
    const canEditProjectCode = mode === 'add'
      ? canCreateProjectCode()
      : (isManageCodeMode || canUpdateProjectCode(projectData));

    const canEditProjectName = mode === 'add'
      ? true
      : canUpdateProjectName(projectData);

    return (
    <FormContainer>
        {/* Business Unit Selection - Always visible at the top */}
        <FormSection>
          <SectionTitle>Business Unit</SectionTitle>
          {mode === 'add' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a business unit first. This will determine the available project settings and configurations.
            </Typography>
          )}
          <FormRow>
            <BusinessUnitSelector
              value={formData.businessUnitId}
              onChange={handleInputChange('businessUnitId')}
              label="Business Unit"
              required={true}
              disabled={projectDataReadonly || isManageCodeMode}
              showStructureType
              fullWidth
            />
          </FormRow>
        </FormSection>

        {/* Show rest of form only when business unit is selected for new projects */}
        {(mode === 'edit' || mode === 'manage-employees' || mode === 'manage-code' || businessUnitSelected) && (
          <>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>
              <FormRow>
                <TextField
                  fullWidth
                  label="Project Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  disabled={projectDataReadonly || !canEditProjectName}
                />
                <TextField
                  fullWidth
                  label="Project Code"
                  value={formData.code || ''}
                  onChange={handleInputChange('code')}
                  disabled={projectDataReadonly || !canEditProjectCode}
                />
              </FormRow>
              <FormRow>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  multiline
                  rows={4}
                  disabled={projectDataReadonly || isManageCodeMode}
                />
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Project Timeline</SectionTitle>
              <FormRow>
                <DatePicker
                  label="Start Date"
                  value={toPickerValue(formData.startDate)}
                  disabled={projectDataReadonly || isManageCodeMode}
                  onChange={(newValue) => {
                    if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                      setFormData(prev => ({
                        ...prev,
                        startDate: newValue.format('YYYY-MM-DD')
                      }));
                    } else if (newValue === null) {
                      setFormData(prev => ({
                        ...prev,
                        startDate: null
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      inputProps: {
                        placeholder: 'DD/MM/YYYY'
                      }
                    }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={toPickerValue(formData.endDate)}
                  disabled={projectDataReadonly || isManageCodeMode}
                  onChange={(newValue) => {
                    if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                      setFormData(prev => ({
                        ...prev,
                        endDate: newValue.format('YYYY-MM-DD')
                      }));
                    } else if (newValue === null) {
                      setFormData(prev => ({
                        ...prev,
                        endDate: null
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      inputProps: {
                        placeholder: 'DD/MM/YYYY'
                      }
                    }
                  }}
                />
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Project Details</SectionTitle>
              <FormRow>
                <ProjectScopeTypeSelector
                  value={formData.scope}
                  onChange={handleInputChange('scope')}
                  label="Project Scope"
                  disabled={projectDataReadonly || isManageCodeMode}
                  fullWidth
                />
                <ProjectPurposeSelector
                  value={formData.projectPurposeId}
                  onChange={handleInputChange('projectPurposeId')}
                  label="Project Purpose"
                  disabled={projectDataReadonly || isManageCodeMode || !orgUnitTypeId}
                  fullWidth
                  orgUnitTypeId={orgUnitTypeId}
                />
              </FormRow>
              <FormRow>
                <BusinessAreaSelector
                  value={formData.businessAreaId}
                  onChange={handleInputChange('businessAreaId')}
                  label="Business Area"
                  disabled={projectDataReadonly || isManageCodeMode}
                  fullWidth
                />
                <div style={{ flex: 1 }} />
              </FormRow>
              <FormRow>
                <ClientSelector
                  value={formData.clientId}
                  onChange={handleInputChange('clientId')}
                  label="Client"
                  required={true}
                  disabled={projectDataReadonly || isManageCodeMode}
                />
                <BillingTypeSelector
                  value={formData.billingType}
                  onChange={handleInputChange('billingType')}
                  label="Billing Type"
                  disabled={projectDataReadonly || isManageCodeMode}
                  showEmptyOption={false}
                  fullWidth
                />
              </FormRow>
              <FormRow>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timesheetRequired}
                        onChange={(e) => handleInputChange('timesheetRequired')(e.target.checked)}
                        disabled={projectDataReadonly || isManageCodeMode}
                      />
                    }
                    label="Timesheet Required"
                    sx={{ ml: 1 }}
                  />
                </Box>
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Contract & Management</SectionTitle>
              <FormRow>
                <ClientContractTypeSelector
                  value={formData.contractTypeId}
                  onChange={handleInputChange('contractTypeId')}
                  label="Contract Type"
                  disabled={projectDataReadonly || isManageCodeMode}
                />
              </FormRow>
              <FormRow>
                <Box sx={{ width: '100%' }}>
                  <EmployeeSearch
                    disabled={mode !== 'add' || projectDataReadonly}
                    value={selectedManager}
                    onChange={handleManagerChange}
                    label="Representative"
                    placeholder={mode === 'add' ? 'Select a representative (optional)' : 'No representative assigned'}
                    height="56px"
                    showSelectedAvatar={true}
                    sx={{ width: '100%' }}
                  />
                  {mode !== 'add' && (
                    <Typography variant="body2" sx={{ mt: 1, color: '#94A3B8', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                      To assign or change the representative, use the Team Structure tab and click the history icon
                      <Box component="span" sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        backgroundColor: '#8B5CF6',
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        lineHeight: 1
                      }}>h</Box>
                      on the project node.
                    </Typography>
                  )}
                </Box>
              </FormRow>
            </FormSection>
          </>
        )}
    </FormContainer>
    );
  };

  // Determine if a specific tab should be readonly based on mode
  const isTabReadonly = (tab) => {
    if (readOnly) return true; // Global readonly takes precedence

    if (mode === 'manage-employees') {
      // In manage-employees mode: PROJECT_DATA and TEAM_STRUCTURE are readonly, EMPLOYEES is editable
      if (tab === TABS.PROJECT_DATA || tab === TABS.TEAM_STRUCTURE) {
        return true;
      }
      if (tab === TABS.EMPLOYEES) {
        return false;
      }
    }

    if (mode === 'manage-code') {
      // In manage-code mode: Only PROJECT_DATA tab is accessible, but with special handling
      // All other tabs are readonly
      if (tab !== TABS.PROJECT_DATA) {
        return true;
      }
    }

    return readOnly;
  };

  const renderContent = () => {
    switch (activeTab) {
      case TABS.PROJECT_DATA:
        return renderProjectData();
      case TABS.TEAM_STRUCTURE:
        return <TeamStructure projectId={id} showAlert={showAlert} readOnly={isTabReadonly(TABS.TEAM_STRUCTURE)} onProjectManagerChange={setSelectedManager} />;
      case TABS.EMPLOYEES:
        return <Employees projectId={id} projectData={projectData} readOnly={isTabReadonly(TABS.EMPLOYEES)} mode={mode} />;
      case TABS.TECHNOLOGY_RADAR:
        return shouldHideAdvancedTabs() ? null : <ProjectRadar projectData={projectData} readOnly={isTabReadonly(TABS.TECHNOLOGY_RADAR)} />;
      case TABS.DASHBOARDS:
        return shouldHideAdvancedTabs() ? null : <Dashboards projectId={id} readOnly={isTabReadonly(TABS.DASHBOARDS)} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => {
              if (onBack) {
                onBack();
              } else {
                navigate('/projects');
              }
            }}>
              <FaArrowLeft /> Back to Projects
            </BackButton>
            <Title>
              {mode === 'add'
                ? 'Create New Project'
                : mode === 'manage-employees'
                ? `Manage Individuals | ${formData.name}`
                : mode === 'manage-code'
                ? `Manage Code | ${formData.name}`
                : readOnly
                ? `View Project | ${formData.name}`
                : `Edit Project | ${formData.name}`}
            </Title>
          </HeaderLeft>
          {!readOnly && mode !== 'manage-employees' && activeTab === TABS.PROJECT_DATA && (mode === 'edit' || mode === 'manage-code' || businessUnitSelected) && (
            <HeaderButton onClick={handleSubmit}>
              {mode === 'add' ? 'Create Project' : 'Update Project'}
            </HeaderButton>
          )}
        </Header>

        <TabContainer>
          <TabButton
            data-active={activeTab === TABS.PROJECT_DATA}
            onClick={() => setActiveTab(TABS.PROJECT_DATA)}
          >
            Project Data
          </TabButton>
          {(mode === 'edit' || mode === 'manage-employees' || mode === 'manage-code') && (
            <>
              <TabButton
                data-active={activeTab === TABS.TEAM_STRUCTURE}
                onClick={() => setActiveTab(TABS.TEAM_STRUCTURE)}
              >
                Team Structure
              </TabButton>
              <TabButton
                data-active={activeTab === TABS.EMPLOYEES}
                onClick={() => setActiveTab(TABS.EMPLOYEES)}
              >
                Individuals
              </TabButton>
              {!shouldHideAdvancedTabs() && (mode === 'edit' || mode === 'manage-code') && (
                <>
                  <TabButton
                    data-active={activeTab === TABS.TECHNOLOGY_RADAR}
                    onClick={() => setActiveTab(TABS.TECHNOLOGY_RADAR)}
                  >
                    Technology Radar
                  </TabButton>
                  <TabButton
                    data-active={activeTab === TABS.DASHBOARDS}
                    onClick={() => setActiveTab(TABS.DASHBOARDS)}
                  >
                    Dashboards
                  </TabButton>
                </>
              )}
            </>
          )}
        </TabContainer>

        <Content>
          {renderContent()}
        </Content>
      </Container>
    </LocalizationProvider>
  );
};

export default ProjectForm; 